import { useEffect, useState } from "react";
import { masterFetch } from "./masterApi";
import MasterLayout from "./MasterLayout";

export default function MasterTenants() {
  const [tenants, setTenants] = useState([]);
  const [plans, setPlans] = useState([]);
  const [err, setErr] = useState("");
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ name: "", owner_email: "", plan: "Standard", data_region: "EU", initial_password: "" });
  const [createdCreds, setCreatedCreds] = useState(null);

  async function load() {
    try {
      const data = await masterFetch("/tenants");
      setTenants(data.tenants || []);
      const p = await masterFetch("/plans").catch(() => ({ plans: [] }));
      setPlans((p.plans || []).filter(x => x.is_active && x.stripe_price_id));
    } catch (e) {
      setErr(e.message);
    }
  }

  useEffect(() => { load(); }, []);

  async function submit(e) {
    e.preventDefault();
    try {
      const data = await masterFetch("/tenants", { method: "POST", body: JSON.stringify(form) });
      setCreatedCreds({ email: form.owner_email, password: data.initial_password });
      setForm({ name: "", owner_email: "", plan: "Standard", data_region: "EU", initial_password: "" });
      setCreating(false);
      load();
    } catch (e) {
      alert(e.message);
    }
  }

  const [subscribingFor, setSubscribingFor] = useState(null);
  const [selectedPlanId, setSelectedPlanId] = useState("");

  async function action(id, kind) {
    if (kind === "nuke") {
      if (!window.confirm("This will PERMANENTLY delete ALL data for this tenant.\n\nThis is irreversible. Type the tenant id in next prompt to confirm.")) return;
      const typed = window.prompt(`Type ${id} to confirm:`);
      if (typed !== id) return;
      try {
        await masterFetch(`/tenants/${id}/nuke`, { method: "POST", body: JSON.stringify({ confirm: "NUKE" }) });
        load();
      } catch (e) { alert(e.message); }
      return;
    }
    if (kind === "reject") {
      const reason = window.prompt("Reason for rejection (optional):") || "manual";
      try {
        await masterFetch(`/tenants/${id}/reject`, { method: "POST", body: JSON.stringify({ reason }) });
        load();
      } catch (e) { alert(e.message); }
      return;
    }
    if (kind === "subscribe") {
      if (plans.length === 0) {
        alert("No active plans with Stripe Price ID. Go to Plans first.");
        return;
      }
      setSelectedPlanId(plans[0].id);
      setSubscribingFor(id);
      return;
    }
    try {
      await masterFetch(`/tenants/${id}/${kind}`, { method: "POST", body: "{}" });
      load();
    } catch (e) { alert(e.message); }
  }

  async function confirmSubscribe() {
    try {
      const data = await masterFetch(`/tenants/${subscribingFor}/subscribe`, {
        method: "POST",
        body: JSON.stringify({ plan_id: selectedPlanId }),
      });
      setSubscribingFor(null);
      window.open(data.checkout_url, "_blank");
    } catch (e) {
      alert("Could not create subscription: " + e.message);
    }
  }

  return (
    <MasterLayout>
      <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", marginBottom: 4 }}>
        <h1 style={{ margin: 0, fontSize: 26, letterSpacing: -0.5 }}>Tenants</h1>
        <button data-testid="master-add-tenant" onClick={() => { setCreating(true); setCreatedCreds(null); }} style={btnPrimary}>+ New tenant</button>
      </div>
      <div style={{ opacity: 0.55, fontSize: 13, marginBottom: 24 }}>
        Companies leasing this platform.
      </div>

      {err && <div style={{ padding: 14, background: "rgba(255,80,90,0.1)", border: "1px solid rgba(255,80,90,0.4)", color: "#ff7886", borderRadius: 8, fontSize: 13, marginBottom: 16 }}>{err}</div>}

      {createdCreds && (
        <div data-testid="master-created-creds" style={{ padding: 16, background: "rgba(16,185,129,0.08)", border: "1px solid rgba(16,185,129,0.35)", borderRadius: 10, marginBottom: 20, fontSize: 13 }}>
          <strong>Tenant created.</strong> Send these credentials to the new admin:<br />
          <code style={{ display: "block", marginTop: 8, padding: 10, background: "#0a0816", borderRadius: 6 }}>
            {createdCreds.email} / {createdCreds.password}
          </code>
        </div>
      )}

      {subscribingFor && (
        <div data-testid="subscribe-modal" style={{ padding: 18, background: "#0f0a1c", border: "1px solid rgba(139,92,246,0.4)", borderRadius: 10, marginBottom: 20 }}>
          <div style={{ fontSize: 11, letterSpacing: 2, opacity: 0.6, marginBottom: 8 }}>SELECT SUBSCRIPTION PLAN</div>
          <select
            data-testid="plan-select"
            value={selectedPlanId}
            onChange={(e) => setSelectedPlanId(e.target.value)}
            style={{ width: "100%", padding: "10px 12px", background: "#06050d", border: "1px solid #3a2a55", borderRadius: 8, color: "#fff", fontSize: 13, fontFamily: "inherit", marginBottom: 12 }}
          >
            {plans.map(p => (
              <option key={p.id} value={p.id}>
                {p.name} — {p.price_monthly} {p.currency}/month
              </option>
            ))}
          </select>
          <div style={{ display: "flex", gap: 8 }}>
            <button data-testid="confirm-subscribe" onClick={confirmSubscribe} style={btnPrimary}>Create Checkout link</button>
            <button onClick={() => setSubscribingFor(null)} style={btnGhost}>Cancel</button>
          </div>
        </div>
      )}

      {creating && (
        <form onSubmit={submit} style={{ background: "#0f0a1c", border: "1px solid #1f1232", padding: 20, borderRadius: 12, marginBottom: 24 }}>
          <Field label="Company name" required>
            <input data-testid="tenant-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required style={input} />
          </Field>
          <Field label="Owner email (admin login)" required>
            <input data-testid="tenant-email-input" type="email" value={form.owner_email} onChange={(e) => setForm({ ...form, owner_email: e.target.value })} required style={input} />
          </Field>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
            <Field label="Plan">
              <select value={form.plan} onChange={(e) => setForm({ ...form, plan: e.target.value })} style={input}>
                <option>Standard</option><option>Pro</option><option>Enterprise</option>
              </select>
            </Field>
            <Field label="Data region">
              <select value={form.data_region} onChange={(e) => setForm({ ...form, data_region: e.target.value })} style={input}>
                <option value="EU">EU (GDPR)</option><option value="US">US (CCPA)</option>
              </select>
            </Field>
          </div>
          <Field label="Initial password (leave blank to auto-generate)">
            <input value={form.initial_password} onChange={(e) => setForm({ ...form, initial_password: e.target.value })} style={input} placeholder="auto-generated" />
          </Field>
          <div style={{ display: "flex", gap: 10, marginTop: 12 }}>
            <button data-testid="tenant-submit" type="submit" style={btnPrimary}>Create tenant</button>
            <button type="button" onClick={() => setCreating(false)} style={btnGhost}>Cancel</button>
          </div>
        </form>
      )}

      <div data-testid="master-tenants-table" style={{ background: "#0f0a1c", border: "1px solid #1f1232", borderRadius: 12, overflow: "hidden" }}>
        <div style={{ display: "grid", gridTemplateColumns: "2fr 2fr 1fr 1fr 2fr", padding: "12px 16px", fontSize: 11, letterSpacing: 2, opacity: 0.5, borderBottom: "1px solid #1f1232" }}>
          <span>NAME</span><span>OWNER</span><span>PLAN</span><span>STATUS</span><span style={{ textAlign: "right" }}>ACTIONS</span>
        </div>
        {tenants.length === 0 && <div style={{ padding: 20, opacity: 0.4, fontSize: 13 }}>No tenants yet.</div>}
        {tenants.map((t) => {
          const isPending = t.approval_status === "pending";
          const isRejected = t.approval_status === "rejected";
          return (
          <div key={t.id} data-testid={`tenant-row-${t.id}`} style={{ display: "grid", gridTemplateColumns: "2fr 2fr 1fr 1fr 2fr", padding: "14px 16px", borderBottom: "1px solid #1a1029", alignItems: "center", fontSize: 13, background: isPending ? "rgba(245,158,11,0.04)" : "transparent" }}>
            <span>
              <div style={{ fontWeight: 500 }}>
                {t.name}
                {isPending && <span data-testid={`tenant-pending-badge-${t.id}`} style={{ marginLeft: 8, fontSize: 10, padding: "2px 8px", borderRadius: 999, background: "rgba(245,158,11,0.15)", color: "#fbbf24", border: "1px solid rgba(245,158,11,0.4)", letterSpacing: 1, fontWeight: 600 }}>PENDING APPROVAL</span>}
                {isRejected && <span style={{ marginLeft: 8, fontSize: 10, padding: "2px 8px", borderRadius: 999, background: "rgba(239,68,68,0.15)", color: "#f87171", border: "1px solid rgba(239,68,68,0.4)", letterSpacing: 1, fontWeight: 600 }}>REJECTED</span>}
                {t.self_signup && !isPending && !isRejected && <span style={{ marginLeft: 8, fontSize: 10, padding: "2px 8px", borderRadius: 999, background: "rgba(16,185,129,0.12)", color: "#34d399", border: "1px solid rgba(16,185,129,0.35)", letterSpacing: 1, fontWeight: 600 }}>SELF-SIGNUP</span>}
              </div>
              <div style={{ opacity: 0.4, fontSize: 11 }}>{t.id.slice(0, 8)}{t.is_default ? " · default" : ""}</div>
            </span>
            <span style={{ opacity: 0.8 }}>{t.owner_email}</span>
            <span style={{ opacity: 0.7 }}>{t.plan}</span>
            <StatusPill status={t.status} />
            <span style={{ display: "flex", gap: 6, justifyContent: "flex-end", flexWrap: "wrap" }}>
              {isPending && (
                <>
                  <button data-testid={`tenant-approve-${t.id}`} onClick={() => action(t.id, "approve")} style={btnApprove}>Approve</button>
                  <button data-testid={`tenant-reject-${t.id}`} onClick={() => action(t.id, "reject")} style={btnDanger}>Reject</button>
                </>
              )}
              {!isPending && !t.is_default && <button onClick={() => action(t.id, "subscribe")} style={btnSmall}>Subscribe</button>}
              {!isPending && !t.is_default && t.status !== "frozen" && <button onClick={() => action(t.id, "freeze")} style={btnSmall}>Freeze</button>}
              {!isPending && !t.is_default && t.status === "frozen" && <button onClick={() => action(t.id, "activate")} style={btnSmall}>Unfreeze</button>}
              {!isPending && !t.is_default && t.status !== "deactivated" && <button onClick={() => action(t.id, "deactivate")} style={btnSmall}>Deactivate</button>}
              {!isPending && !t.is_default && t.status === "deactivated" && <button onClick={() => action(t.id, "activate")} style={btnSmall}>Reactivate</button>}
              {!t.is_default && <button onClick={() => action(t.id, "nuke")} data-testid={`tenant-nuke-${t.id}`} style={btnDanger}>Nuke</button>}
            </span>
          </div>
          );
        })}
      </div>
    </MasterLayout>
  );
}

function Field({ label, required, children }) {
  return (
    <div style={{ marginBottom: 12 }}>
      <label style={{ display: "block", fontSize: 11, letterSpacing: 1.5, opacity: 0.7, marginBottom: 6, textTransform: "uppercase" }}>
        {label}{required && " *"}
      </label>
      {children}
    </div>
  );
}

function StatusPill({ status }) {
  const styles = {
    active: { bg: "rgba(16,185,129,0.15)", color: "#34d399", border: "rgba(16,185,129,0.4)" },
    frozen: { bg: "rgba(245,158,11,0.15)", color: "#fbbf24", border: "rgba(245,158,11,0.4)" },
    deactivated: { bg: "rgba(239,68,68,0.15)", color: "#f87171", border: "rgba(239,68,68,0.4)" },
  };
  const s = styles[status] || styles.active;
  return <span style={{ background: s.bg, color: s.color, border: `1px solid ${s.border}`, padding: "3px 10px", borderRadius: 999, fontSize: 11, textTransform: "uppercase", letterSpacing: 1, fontWeight: 600, width: "fit-content" }}>{status}</span>;
}

const input = { width: "100%", padding: "10px 12px", background: "#06050d", border: "1px solid #3a2a55", borderRadius: 8, color: "#fff", fontSize: 13, fontFamily: "inherit" };
const btnPrimary = { padding: "9px 16px", background: "linear-gradient(135deg,#8b5cf6,#a855f7)", color: "#fff", border: "none", borderRadius: 8, cursor: "pointer", fontWeight: 600, fontSize: 13, fontFamily: "inherit" };
const btnGhost = { padding: "9px 16px", background: "transparent", color: "#a89ec9", border: "1px solid #3a2a55", borderRadius: 8, cursor: "pointer", fontSize: 13, fontFamily: "inherit" };
const btnSmall = { padding: "5px 10px", background: "transparent", color: "#a89ec9", border: "1px solid #3a2a55", borderRadius: 6, cursor: "pointer", fontSize: 11, fontFamily: "inherit" };
const btnDanger = { padding: "5px 10px", background: "rgba(239,68,68,0.1)", color: "#f87171", border: "1px solid rgba(239,68,68,0.4)", borderRadius: 6, cursor: "pointer", fontSize: 11, fontFamily: "inherit" };
const btnApprove = { padding: "5px 12px", background: "rgba(16,185,129,0.15)", color: "#34d399", border: "1px solid rgba(16,185,129,0.5)", borderRadius: 6, cursor: "pointer", fontSize: 11, fontFamily: "inherit", fontWeight: 600 };
